import { useState, useEffect, useCallback } from "react";
import { Table, Input, Select, Form, Row, Col } from "antd";
import { DropdownCheckBox } from "atoms/UIDropdown/Dropdown";
import {
	SearchOutlined,
	EditOutlined,
	DeleteOutlined,
	EyeOutlined,
	HistoryOutlined,
	CopyOutlined,
} from "@ant-design/icons";
import { Modal, message, Button, Tooltip } from "antd";
import AddTask from "organisms/AddTask";
import EditTask from "organisms/EditTask";
import { CALL_FEATCH_API } from "common/API";
import { connect } from "react-redux";
import { useSelector } from "react-redux";
import { getUserInfo, getCountryList } from "redux/selectors";
import moment from "moment";
import TaskFilterModal from "./TaskFilterModal";
import styled from "styled-components";
import COLORS from "common/Colors";

const TaskWrap = styled.div`
.filters-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    margin-bottom: 1rem;
    gap: 10px;
  }
  .total-count {
    font-weight: 600;
    font-size: 16px;
    color: ${COLORS.TEXT.PRIMARY};
  }
  .filters {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 10px;
    flex-wrap: nowrap;
  }
  .action-icons {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 10px;
    opacity: 0;
    transition: opacity 0.2s ease;
    }
  .ant-table-tbody > tr:hover .action-icons { opacity: 1; }
  .filter-input { min-width: 180px; max-width: 220px; }
  .filter-input.ant-input-affix-wrapper {
    border-radius: 20px !important;
    height: 38px !important;
    display: flex;
    align-items: center;
    padding: 0 11px;
    border: 1px solid #d9d9d9;
    }
  .filter-input.ant-input-affix-wrapper .ant-input {
    border: none !important;
    box-shadow: none !important;
  }
  .filter-input.ant-input-affix-wrapper:hover,
  .filter-input.ant-input-affix-wrapper:focus-within {
    border-color: ${COLORS.PRIMARY} !important;
    box-shadow: none !important;
  }
  .filter-input .ant-input-prefix { margin-right: 6px; }
  .filter-input .ant-select-selector {
    border-radius: 20px !important;
    height: 38px !important;
    display: flex;
    align-items: center;
  }
  .filter-input .ant-select-selector:hover,
  .ant-select-focused .ant-select-selector {
    border-color: ${COLORS.PRIMARY} !important;
    box-shadow: none !important;
  }
`;

const StyledModal = styled(Modal)`
  .ant-modal-content { border-radius: 12px; }
  .ant-modal-header { border-radius: 12px 12px 0 0; }
  `;

const ActionIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 18px;
  background: #deefee;
  color: #00685F !important;
  transition: opacity 0.15s;
`;

const CancelBtn = styled(Button)`
  && {
    height: 38px;
    padding: 0 24px;
    border-radius: 8px;
    border: 1px solid #000000;
    color: #000000;
    font-weight: 500;
    background: #fff;
  }

  &&:hover {
    border-color: #9ca3af;
    color: #111827;
    background: #f9fafb;
  }
  `;

const ConfirmBtn = styled(Button)`
  && {
    height: 38px;
    padding: 0 28px;
    border-radius: 8px;
     background: #00685F;
    border: none;
    color: #ffffff !important;
    font-weight: 600;
    }

    &&:hover,
  &&:focus {
    background-color: #00685F !important;
    color: #111827 !important;
    border: none;
    }
    `;

const StyledSearchInput = styled(Input)`
  width: 200px;
  &.ant-input-affix-wrapper { border-radius: 15px !important; }
  .ant-input-prefix { color: #aaa; }
`;

const PAGE_LIMIT = 10;

// When `matterId` is provided the component runs in "matter mode":
//   - fetches only tasks for that matter (adds matterid param to API)
//   - hides the breadcrumb and the "Matter" column
//   - uses matter-scoped pagination/filters
// When `matterId` is absent it behaves exactly as before (all-tasks mode).
function AllTasks({ onBack, userInfo, matterId, matterName }) {
	const countryList = useSelector(getCountryList);
	const isMatterMode = Boolean(matterId);
	const [taskList, setTaskList] = useState([]);
	const [loading, setLoading] = useState(false);
	const [total, setTotal] = useState(0);
	const [currentPage, setCurrentPage] = useState(1);
	const [searchText, setSearchText] = useState("");
	const [filterStatus, setFilterStatus] = useState([]);
	const [filterAssignee, setFilterAssignee] = useState([]);
	const [filterPriority, setFilterPriority] = useState([]);

	const [filterOptions, setFilterOptions] = useState({ statuses: [], assignees: [], priorities: [], createdBy: [], taskTypes: [] });
	const [appliedFilters, setAppliedFilters] = useState(null);
	const [matterList, setMatterList] = useState([]);
	const [editingTask, setEditingTask] = useState(false);
	const [taskDetails, setTaskDetails] = useState({});
	const [deleteModal, setDeleteModal] = useState(false);
	const [deleteRecord, setDeleteRecord] = useState(null);
	const [historyModal, setHistoryModal] = useState(false);
	const [historyRecord, setHistoryRecord] = useState(null);
	const [historyData, setHistoryData] = useState([]);
	const [historyLoading, setHistoryLoading] = useState(false);
	const [cloningTask, setCloningTask] = useState(false);
	const [cloneDetails, setCloneDetails] = useState({});
	const [statusModal, setStatusModal] = useState(false);
	const [statusRecord, setStatusRecord] = useState(null);
	const [newStatus, setNewStatus] = useState(null);
	const [statusOptions, setStatusOptions] = useState([]);
	const [viewModal, setViewModal] = useState(false);
	const [viewRecord, setViewRecord] = useState(null);

	const [refreshKey, setRefreshKey] = useState(0);
	const [form] = Form.useForm();
	const { Option } = Select;



	// ── Fetch status options ────────────────────────────────────────────────
	useEffect(() => {
		const fetchStatuses = async () => {
			const res = await CALL_FEATCH_API(`insyts-task/gettaskstatus?userid=${userInfo?.userid}&page=1&limit=500`, "GET");
			if (res?.response?.code === "SUCCESS") {
				setStatusOptions(res.response.data.map(s => ({ label: s.statusname, value: s.statusname })));
			}
		};
		if (userInfo?.userid) fetchStatuses();
	}, [userInfo?.userid]);

	// ── Fetch filter options ────────────────────────────────────────────────
	const fetchFilterOptions = useCallback(async () => {
		const url = isMatterMode
			? `insyts-task/gettask?userid=${userInfo?.userid}&filterdata=true&matterid=${matterId}`
			: `insyts-task/gettask?userid=${userInfo?.userid}&filterdata=true&all=true`;
		const res = await CALL_FEATCH_API(url, "GET");
		if (res?.response?.code === "SUCCESS") {
			setFilterOptions(res.response.data);
		}
	}, [userInfo?.userid, isMatterMode, matterId]);

	useEffect(() => {
		if (userInfo?.userid) fetchFilterOptions();
	}, [userInfo?.userid, fetchFilterOptions]);

	// ── Fetch task history ──────────────────────────────────────────────────
	const fetchTaskHistory = async (record) => {
		setHistoryLoading(true);
		setHistoryData([]);
		try {
			const res = await CALL_FEATCH_API(
				`insyts-task/gettaskhistory?taskid=${record.taskid}&userid=${userInfo?.userid}`,
				"GET"
			);
			if (res?.response?.code === "SUCCESS") {
				setHistoryData(res.response.data || []);
			}
		} catch (_) { }
		setHistoryLoading(false);
	};

	// ── Action handlers ─────────────────────────────────────────────────────
	const handleEdit = (e, record) => {
		e.stopPropagation();
		setTaskDetails(record);
		setEditingTask(true);
	};

	const handleDelete = (e, record) => {
		e.stopPropagation();
		setDeleteRecord(record);
		setDeleteModal(true);
	};

	const handleDeleteConfirm = async () => {
		const response = await CALL_FEATCH_API(`insyts-task/deletetask`, "post", {
			taskid: deleteRecord.taskid,
			userid: userInfo?.userid,
			deletedby: userInfo?.username || userInfo?.userid || "",
		});
		if (
			response?.code === "SUCCESS" ||
			response?.response?.code === "SUCCESS"
		) {
			message.success("Task deleted successfully.");
			setFilterOptions({ statuses: [], assignees: [], priorities: [], createdBy: [], taskTypes: [] });
			fetchAllTasks(currentPage);
			fetchFilterOptions();
		} else {
			message.error("Failed to delete task. Please try again.");
		}
		setDeleteModal(false);
		setDeleteRecord(null);
	};

	const handleStatusUpdate = async () => {
		const response = await CALL_FEATCH_API("insyts-task/edittask", "post", {
			taskid: statusRecord.taskid,
			userid: userInfo?.userid,
			userName: userInfo?.name || null,
			taskstatus: newStatus,
			updatedby: userInfo?.userid || "",
			statusOnly: true,
		});
		if (response?.code === "SUCCESS" || response?.response?.code === "SUCCESS") {
			message.success("Status updated successfully.");
			setFilterOptions({ statuses: [], assignees: [], priorities: [], createdBy: [], taskTypes: [] });
			fetchAllTasks(currentPage);
			fetchFilterOptions();
		} else {
			message.error("Failed to update status.");
		}
		setStatusModal(false);
		setStatusRecord(null);
		setNewStatus(null);
	};

	// ── Fetch matter list ───────────────────────────────────────────────────
	useEffect(() => {
		const fetchMatters = async () => {
			try {
				const res = await CALL_FEATCH_API(
					`insyts-matter/getmatter?userid=${userInfo?.userid}&managingoffice=${userInfo?.managingOffice || countryList[0]?.countryname}&page=1&limit=100`,
					"GET"
				);
				const rawList = res?.response?.data || res?.data || [];
				setMatterList(
					rawList.map((m) => ({
						matterid: m.matterId || m.matterid,
						mattername: m.jobTitle || m.information?.jobTitle || m.mattername || "",
					}))
				);
			} catch (_) { }
		};
		if (userInfo?.userid) fetchMatters();
	}, [userInfo?.userid]);

	const handleFormFilterChange = () => {
		const values = form.getFieldsValue();
		setSearchText(values.search || "");
		setFilterStatus(values.status || []);
		setFilterAssignee(values.assignee || []);
		setFilterPriority(values.priority || []);

	};

	// ── Fetch tasks — server-side filters + pagination ──────────────────────
	const fetchAllTasks = useCallback(
		async (page = 1) => {
			setLoading(true);

			const params = new URLSearchParams({
				userid: userInfo?.userid,
				page: String(page),
				limit: String(PAGE_LIMIT),
				// in matter mode we scope by matterid; in all-tasks mode we fetch all
				...(isMatterMode ? { matterid: matterId } : { all: "true" }),
			});

			// ── Inline filters (sent to API) ──────────────────────────────────
			if (filterStatus?.length) params.append("taskstatus", filterStatus.join(","));
			if (filterAssignee?.length) params.append("assignedtoname", filterAssignee.join(","));
			if (filterPriority?.length) params.append("priority", filterPriority.join(","));


			// ── TaskFilterModal filters (sent to API) ─────────────────────────
			if (appliedFilters) {
				if (appliedFilters.tasktype?.length)
					params.append("tasktype", appliedFilters.tasktype.join(","));
				if (appliedFilters.matterid?.length)
					params.append("matterid", appliedFilters.matterid.join(","));
				if (appliedFilters.createdby?.length)
					params.append("createdbyname", appliedFilters.createdby.join(","));
				if (appliedFilters.isprivate !== null && appliedFilters.isprivate !== undefined)
					params.append("isprivate", appliedFilters.isprivate);
				if (appliedFilters.dueDateFrom)
					params.append("duedatefrom", moment(appliedFilters.dueDateFrom).format("YYYY-MM-DD"));
				if (appliedFilters.dueDateTo)
					params.append("duedateto", moment(appliedFilters.dueDateTo).format("YYYY-MM-DD"));
				if (appliedFilters.createdDateFrom)
					params.append("createddatefrom", moment(appliedFilters.createdDateFrom).format("YYYY-MM-DD"));
				if (appliedFilters.createdDateTo)
					params.append("createddateto", moment(appliedFilters.createdDateTo).format("YYYY-MM-DD"));
			}

			try {
				const res = await CALL_FEATCH_API(
					`insyts-task/gettask?${params.toString()}`,
					"GET"
				);
				if (res?.response?.code === "SUCCESS") {
					setTaskList(res.response.data || []);
					// ✅ Use real total from API — same as Task component
					setTotal(
						res.response.total ??
						res.response.pagination?.total ??
						res.response.data?.length ??
						0
					);
					setCurrentPage(page);
				}
			} catch (_) { }

			setLoading(false);
		},
		[userInfo?.userid, filterStatus, filterAssignee, filterPriority, appliedFilters, isMatterMode, matterId]
	);

	// ── Re-fetch from page 1 whenever any filter changes ───────────────────
	useEffect(() => {
		if (userInfo?.userid) {
			setCurrentPage(1);
			fetchAllTasks(1);
		}
	}, [userInfo?.userid, filterStatus, filterAssignee, filterPriority, appliedFilters, refreshKey, matterId]);

	// ── Client-side search only (no page reset needed) ─────────────────────
	const displayData = taskList.filter((item) =>
		!searchText?.length ||
		item.taskname?.toLowerCase().includes(searchText.toLowerCase())
	);

	const taskTypeOptions = filterOptions.taskTypes;
	const createdByOptions = filterOptions.createdBy;

	// In matter mode, derive assignees from actual task list so only
	// assignees relevant to this matter's tasks appear in the dropdown.
	const assigneeOptions = isMatterMode
		? [
			...new Map(
				taskList
					.filter((t) => t.assignedtoname)
					.map((t) => [t.assignedtoname, { label: t.assignedtoname, value: t.assignedtoname }])
			).values(),
		]
		: filterOptions?.assignees?.map((a) => ({ label: a, value: a })) || [];

	const columns = [
		{ title: "Name", dataIndex: "taskname" },
		{
			title: "Created by",
			render: (_, record) => (
				<div>
					<div style={{ fontSize: 14, color: "#111827" }}>{record.createdbyname || "N/A"}</div>
					<small style={{ fontSize: 12, color: "#6b7280", fontStyle: "italic" }}>
						{record.createdat ? moment(record.createdat).format("Do MMM, YYYY") : ""}
					</small>
				</div>
			),
		},
		{ title: "Assigned to", dataIndex: "assignedtoname" },
		{ title: "Priority", dataIndex: "priority" },
		{ title: "Status", dataIndex: "taskstatus" },
		{ title: "Type", dataIndex: "tasktype" },
		// Hide the Matter column when scoped to a single matter — it's redundant
		...(!isMatterMode ? [{ title: "Matter", dataIndex: "mattername" }] : []),
		{
			title: "Action",
			key: "action",
			width: 160,
			render: (_, record) => {
				const isCreator = record.createdby === userInfo?.userid;
				const isAssignee = record.assignedto === userInfo?.userid;

				return (
					<div className="action-icons">

						{(isCreator || isAssignee) && (
							<Tooltip title="History">
								<ActionIcon
									onClick={(e) => { e.stopPropagation(); setHistoryRecord(record); setHistoryModal(true); fetchTaskHistory(record); }}>
									<HistoryOutlined />
								</ActionIcon>
							</Tooltip>
						)}

						{(isCreator || isAssignee) && (
							<Tooltip title="Clone">
								<ActionIcon
									onClick={(e) => { e.stopPropagation(); setCloneDetails(record); setCloningTask(true); }}>
									<CopyOutlined />
								</ActionIcon>
							</Tooltip>
						)}

						{isCreator && (
							<Tooltip title="Edit">
								<ActionIcon
									onClick={(e) => handleEdit(e, record)}>
									<EditOutlined />
								</ActionIcon>
							</Tooltip>
						)}

						{isCreator && (
							<Tooltip title="Delete">
								<ActionIcon
									onClick={(e) => handleDelete(e, record)}>
									<DeleteOutlined />
								</ActionIcon>
							</Tooltip>
						)}

						{isAssignee && (
							<Tooltip title="Update Status">
								<ActionIcon

									style={{ width: "auto", padding: "0 12px", fontSize: 12, fontWeight: 600, whiteSpace: "nowrap" }}
									onClick={(e) => { e.stopPropagation(); setStatusRecord(record); setNewStatus(record.taskstatus); setStatusModal(true); }}
								>
									Update Status
								</ActionIcon>
							</Tooltip>
						)}

					</div>
				);
			},
		},
	];

	return (
		<TaskWrap>
			{/* Breadcrumb — only shown in standalone all-tasks mode */}
			{!isMatterMode && (
				<div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 24, fontSize: 14 }}>
					<span onClick={onBack} style={{ cursor: "pointer", color: "#555", fontWeight: 500 }}>
						Task
					</span>
					<span style={{ color: "#bbb", fontSize: 12 }}>›</span>
					<span style={{ color: "#111", fontWeight: 600 }}>All Tasks</span>
				</div>
			)}

			{/* Filters row */}
			<div className="filters-header" style={isMatterMode ? { marginTop: "1.5rem" } : {}}>
				<div className="total-count">Total Tasks: {total}</div>
				<Form form={form} layout="vertical" onValuesChange={handleFormFilterChange}>
					<Row gutter={12} align="bottom" wrap>
						<Col>
							<div style={{ width: 200 }}>
								<Form.Item name="search" style={{ margin: 0, marginBottom: 16 }}>
									<StyledSearchInput
										placeholder="Search"
										prefix={<SearchOutlined />}
										allowClear
									/>
								</Form.Item>
							</div>
						</Col>

						<Col>
							<div style={{ width: 200 }}>
								<DropdownCheckBox
									name="status"
									label="Status"
									options={filterOptions?.statuses?.map((s) => ({ label: s, value: s })) || []}
									form={form}
									handleFormChange={handleFormFilterChange}
								/>
							</div>
						</Col>



						<Col>
							<div style={{ width: 200 }}>
								<DropdownCheckBox
									name="assignee"
									label="Assignee"
									options={assigneeOptions}
									form={form}
									handleFormChange={handleFormFilterChange}
								/>
							</div>
						</Col>

						<Col>
							<div style={{ width: 200 }}>
								<DropdownCheckBox
									name="priority"
									label="Priority"
									options={filterOptions?.priorities?.map((p) => ({ label: p, value: p })) || []}
									form={form}
									handleFormChange={handleFormFilterChange}
								/>
							</div>
						</Col>

						<Col>
							<TaskFilterModal
								taskTypeOptions={taskTypeOptions}
								matterOptions={matterList}
								createdByOptions={createdByOptions}
								onApply={(filters) => setAppliedFilters(filters)}
								onClear={() => { setAppliedFilters(null); form.resetFields(); }}
								hideMatter={isMatterMode}
							/>
						</Col>
					</Row>
				</Form>
			</div>

			{/* Table */}
			<Table
				columns={columns}
				dataSource={displayData}
				loading={loading}
				rowKey="taskid"
				size="small"
				onRow={(record) => ({
					onClick: () => { setViewRecord(record); setViewModal(true); },
					style: { cursor: "pointer" },
				})}
				pagination={{
					current: currentPage,
					pageSize: PAGE_LIMIT,
					total: total,
					onChange: (p) => fetchAllTasks(p),
					showSizeChanger: false,
					showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} tasks`,
				}}
			/>
			{/* Delete Modal */}
			<StyledModal visible={deleteModal} onCancel={() => { setDeleteModal(false); setDeleteRecord(null); }}
				footer={null} width={420} closable={false} centered destroyOnClose>
				<div style={{ textAlign: "center", padding: "24px 16px 8px" }}>
					<p style={{ fontSize: 15, fontWeight: 500, color: "#111827", marginBottom: 32 }}>
						Are you sure you want to delete this Task?
					</p>
					<div style={{ display: "flex", justifyContent: "center", gap: 16 }}>
						<CancelBtn onClick={() => { setDeleteModal(false); setDeleteRecord(null); }} style={{ width: 100 }}>No</CancelBtn>
						<ConfirmBtn onClick={handleDeleteConfirm} style={{ width: 100 }}>Yes</ConfirmBtn>
					</div>
				</div>
			</StyledModal>

			{/* Edit Modal */}
			<EditTask
				isModalVisible={editingTask}
				closeModal={(success) => {
					setEditingTask(false);
					if (success) {
						setFilterOptions({ statuses: [], assignees: [], priorities: [], createdBy: [], taskTypes: [] });
						fetchAllTasks(currentPage);
						fetchFilterOptions();
					}
				}}
				getTaskList={() => fetchAllTasks(currentPage)}
				taskDetails={taskDetails}
			/>

			{/* Clone Modal */}
			<AddTask
				isModalVisible={cloningTask}
				closeModal={() => { setCloningTask(false); setCloneDetails({}); }}
				getTaskList={() => fetchAllTasks(currentPage)}
				isClone={true}
				cloneData={cloneDetails}
				matterList={matterList}
			/>

			{/* History Modal */}
			<StyledModal visible={historyModal}
				onCancel={() => { setHistoryModal(false); setHistoryRecord(null); setHistoryData([]); }}
				footer={null} width={600} title="Task History" destroyOnClose>
				<div style={{ border: "1px solid #f0f0f0", borderRadius: 10, overflow: "hidden" }}>
					<div style={{
						display: "grid", gridTemplateColumns: "1fr 1fr", background: "#f9fafb",
						padding: "12px 20px", fontWeight: 600, fontSize: 14, color: "#374151", borderBottom: "1px solid #f0f0f0"
					}}>
						<span>Action by</span><span>Date &amp; Time</span>
					</div>
					{historyLoading ? (
						<div style={{ textAlign: "center", padding: "32px 0", color: "#888" }}>Loading...</div>
					) : historyData.length === 0 ? (
						<div style={{ textAlign: "center", padding: "32px 0", color: "#888", fontSize: 14 }}>No history available.</div>
					) : (
						historyData.map((h, i) => (
							<div key={i} style={{
								display: "grid", gridTemplateColumns: "1fr 1fr", padding: "16px 20px",
								borderBottom: i < historyData.length - 1 ? "1px solid #f5f5f5" : "none", fontSize: 14, color: "#6b7280", background: "#fff"
							}}>
								<span>{h.action === "Updated status by" ? "Status Updated by" : h.action} <strong style={{ color: "#111827" }}>{h.actionbyname || "N/A"}</strong></span>
								<span>{h.actionat ? moment(h.actionat).format("Do MMM YYYY; hh:mm A") : "—"}</span>
							</div>
						))
					)}
				</div>
			</StyledModal>

			{/* View Modal */}
			<StyledModal
				visible={viewModal}
				onCancel={() => { setViewModal(false); setViewRecord(null); }}
				footer={null}
				width={520}
				destroyOnClose
				title={
					<div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", paddingRight: 24 }}>
						<span style={{ fontWeight: 700, fontSize: 16 }}>Task Details</span>
						<div style={{ display: "flex", alignItems: "center", gap: 10, marginRight: 15 }}>

							{/* History */}
							{(viewRecord?.createdby === userInfo?.userid || viewRecord?.assignedto === userInfo?.userid) && (
								<Tooltip title="History">
									<ActionIcon
										onClick={(e) => {
											e.stopPropagation();
											setHistoryRecord(viewRecord);
											setHistoryModal(true);
											fetchTaskHistory(viewRecord);
											setViewModal(false);
										}}>
										<HistoryOutlined />
									</ActionIcon>
								</Tooltip>
							)}

							{/* Clone */}
							{(viewRecord?.createdby === userInfo?.userid || viewRecord?.assignedto === userInfo?.userid) && (
								<Tooltip title="Clone">
									<ActionIcon
										onClick={(e) => {
											e.stopPropagation();
											setCloneDetails(viewRecord);
											setCloningTask(true);
											setViewModal(false);
										}}>
										<CopyOutlined />
									</ActionIcon>
								</Tooltip>
							)}

							{/* Edit — always visible for creator */}
							{viewRecord?.createdby === userInfo?.userid && (
								<Tooltip title="Edit">
									<ActionIcon
										onClick={(e) => {
											e.stopPropagation();
											setTaskDetails(viewRecord);
											setEditingTask(true);
											setViewModal(false);
										}}>
										<EditOutlined />
									</ActionIcon>
								</Tooltip>
							)}

							{/* Delete — always visible for creator */}
							{viewRecord?.createdby === userInfo?.userid && (
								<Tooltip title="Delete">
									<ActionIcon
										onClick={(e) => {
											e.stopPropagation();
											setDeleteRecord(viewRecord);
											setDeleteModal(true);
											setViewModal(false);
										}}>
										<DeleteOutlined />
									</ActionIcon>
								</Tooltip>
							)}

							{/* Update Status — assignee only, always visible */}
							{viewRecord?.assignedto === userInfo?.userid && (
								<ActionIcon
									style={{ width: "auto", padding: "0 12px", fontSize: 12, fontWeight: 600, whiteSpace: "nowrap" }}
									onClick={(e) => {
										e.stopPropagation();
										setStatusRecord(viewRecord);
										setNewStatus(viewRecord.taskstatus);
										setStatusModal(true);
										setViewModal(false);
									}}
								>
									Update Status
								</ActionIcon>
							)}

						</div>
					</div>
				}
			>
				{viewRecord && (
					<div style={{ fontSize: 13 }}>
						{[
							{ label: "Name of the Task", value: viewRecord.taskname },
							{ label: "Priority", value: viewRecord.priority },
							{ label: "Task Status", value: viewRecord.taskstatus },
							{ label: "Created by", value: `${viewRecord.createdbyname || ""}${viewRecord.createdat ? "; " + moment(viewRecord.createdat).format("Do MMM YYYY") : ""}` },
							{ label: "Assigned to", value: viewRecord.assignedtoname },
							{ label: "Due Date & Time", value: `${viewRecord.duedate ? moment(viewRecord.duedate).format("Do MMM YYYY") : ""}${viewRecord.duetime ? "; " + moment(viewRecord.duetime, "HH:mm").format("hh:mm A") : ""}` },
							{ label: "Task Type", value: viewRecord.tasktype },
							{ label: "Matter", value: viewRecord.mattername },
							{ label: "Estimated Time of Completion", value: viewRecord.estimatedtime },
						].map(({ label, value }) =>
							value ? (
								<div key={label} style={{ display: "flex", padding: "10px 0" }}>
									<span style={{ width: 200, color: "#6b7280", flexShrink: 0 }}>{label}</span>
									<span style={{ color: "#111827", fontWeight: 500 }}>{value}</span>
								</div>
							) : null
						)}
						{viewRecord.description && (
							<div style={{ paddingTop: 14 }}>
								<div style={{ color: "#6b7280", marginBottom: 6 }}>Task Description</div>
								<div style={{ color: "#111827", lineHeight: 1.7 }}>{viewRecord.description}</div>
							</div>
						)}
					</div>
				)}
			</StyledModal>

			{/* Status Modal */}
			<StyledModal visible={statusModal}
				onCancel={() => { setStatusModal(false); setStatusRecord(null); setNewStatus(null); }}
				footer={null} width={480} title="Update Status" destroyOnClose centered>
				<div style={{ padding: "16px 0" }}>
					<Select style={{ width: "100%" }} value={newStatus} onChange={(val) => setNewStatus(val)}
						getPopupContainer={(trigger) => trigger.parentElement}>
						{statusOptions.map((s) => <Option key={s.value} value={s.value}>{s.label}</Option>)}
					</Select>
					<div style={{ display: "flex", justifyContent: "flex-end", gap: 12, marginTop: 32 }}>
						<CancelBtn onClick={() => { setStatusModal(false); setStatusRecord(null); setNewStatus(null); }}>Cancel</CancelBtn>
						<ConfirmBtn onClick={handleStatusUpdate}>Update</ConfirmBtn>
					</div>
				</div>
			</StyledModal>

		</TaskWrap>
	);
}

const mapStateToProps = (state) => ({
	userInfo: getUserInfo(state),
});

export default connect(mapStateToProps, null)(AllTasks);